/**
 * Custom BubbleChartComponent - Override Component
 */
import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { BubbleChartComponent as ParentBubbleChartComponent } from '@shared-lib/chart-components/bubble-chart/bubble-chart.component';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { CustomConfigService } from '../../shared-services/custom-config.service';
import { downloadAsExcel } from '@shared-lib/utils/export.utils';

@Component({
  selector: 'app-bubble-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bubble-chart.component.html',
  styleUrls: ['./bubble-chart.component.scss']
})
export class BubbleChartComponent extends ParentBubbleChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  private resizeObserver!: ResizeObserver;
  private selectedLegends: string[] = [];
  private isCtrlPressed = false;
  private isInternalLegendChange = false;
  private readonly noDataText = 'No Data avaliable try changing filters';
  private _sizeMultiplier = 2;
  private _maxBubbleSize = 70;
  private _seriesSnapshot: any[] = [];
  private rawBubbleData: any[] = [];

  isFullScreen = false;

  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen;

    if (this.isFullScreen) {
      document.documentElement.classList.add('dvm-no-scroll');
      document.body.classList.add('dvm-no-scroll');
    } else {
      document.documentElement.classList.remove('dvm-no-scroll');
      document.body.classList.remove('dvm-no-scroll');

      // Global reset when exiting focus mode
      this.globalFilterService.clearAllFilters();
      this.reset();
    }

    setTimeout(() => this.triggerResize(), 100);
    setTimeout(() => this.triggerResize(), 500);
    setTimeout(() => this.triggerResize(), 1200);

    // Re-render chart to apply symmetrical grid if needed
    if (this.myChart) {
      const option = this.myChart.getOption() as any;
      if (option && option.grid && option.grid[0]) {
        option.grid[0].top = this.isFullScreen ? 200 : '20%';
        option.grid[0].left = this.isFullScreen ? 120 : 80;
        option.grid[0].right = this.isFullScreen ? 120 : '8%';
        option.grid[0].bottom = this.isFullScreen ? 160 : '15%';
        if (option.legend && option.legend[0]) {
          option.legend[0].top = this.isFullScreen ? 80 : 0;
        }
        this.myChart.setOption(option, true);
      }
    }
  }

  /**
   * Resets internal selection state and restores visual highlight.
   */
  public override reset(): void {
    super.reset(); // Clears selectedPointNames
    this.selectedLegends = [];
    this.applyLegendSelectionState(); // Restores visual state
  }

  private triggerResize(): void {
    if (this.myChart && typeof this.myChart.resize === 'function') {
      this.myChart.resize();
    } else {
      window.dispatchEvent(new Event('resize'));
    }
  }

  private resolveChartHeight(): string {
    return (
      this.data?.chartConfig?.chartHeight ||
      this.data?.chartConfig?.chartheight ||
      this.data?.chartHeight ||
      this.data?.chartConfig?.height ||
      '700px'
    );
  }

  constructor(
    configService: CustomConfigService,
    globalFilterService: GlobalFilterService,
    override chartFilterHandler: ChartFilterHandlerService,
  ) {
    super(configService, globalFilterService, chartFilterHandler);
  }

  protected override loadAndRenderChart(filters?: any): Observable<any> {
    try {
      // Legend sync events are for visual cross-selection only; do not trigger API calls.
      if (filters?.legendSync) {
        return of(null);
      }

      if (filters && this.data?.widgetId && !this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        return of(null);
      }

      const widgetId = String(this.data?.widgetId || '').trim();

      if (widgetId && widgetId.includes('Margin % by Region')) {
        if (this.myChart) this.myChart.showLoading();

        return this.configService
          .getConfig(this.data.apiUrl, this.data, filters)
          .pipe(
            switchMap((res: any) => {
              const chartData = res?.data || [];
              if (chartData.length > 0) {
                this.renderCustomBubbleUI(chartData);

                setTimeout(() => {
                  if (this.myChart) {
                    this.myChart.hideLoading();
                    this.myChart.resize();
                  }
                }, 200);
              } else {
                this.showNoDataState();
              }
              // Return of(null) to prevent ParentBubbleChartComponent from calling its own renderChart
              return of(null);
            }),
            catchError((err: any) => {
              console.error('[BubbleChart] Request Failed:', err);
              this.showNoDataState();
              return of(null);
            })
          );
      }

      return super.loadAndRenderChart(filters);
    } catch (err) {
      console.error('[BubbleChart] Logic Error:', err);
      return super.loadAndRenderChart(filters);
    }
  }

  private showNoDataState(): void {
    const chartDom = this.chartContainer?.nativeElement || document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    const configHeight = this.resolveChartHeight();
    chartDom.style.height = configHeight;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
      this.addResizeListener();
    }

    this.myChart.hideLoading();
    this.myChart.setOption(
      {
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: this.noDataText,
              fill: '#666',
              fontSize: 14,
              fontFamily: 'sans-serif',
            },
          },
        ],
      },
      true,
    );
  }

  private renderCustomBubbleUI(rawData: any[]): void {
    this.rawBubbleData = rawData || [];
    const chartDom = this.chartContainer?.nativeElement || document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    // Apply JSON height from config
    const configHeight = this.resolveChartHeight();
    chartDom.style.height = configHeight;

    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.round(px * fs);

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
      this.addResizeListener();
    }


    const fallbackPalette = this.data?.chartConfig?.topNColorsByStackPosition || ['#DA291C', '#118DFF', '#1AAB40', '#999999', '#FF9933'];
    const regions = Array.from(new Set(rawData.map((d: any) => d.region).filter(Boolean)));
    const engine_model = Array.from(new Set(rawData.map((d: any) => d.engine_model).filter(Boolean)));
    const segment = Array.from(new Set(rawData.map((d: any) => d.segment).filter(Boolean)));

    // Decide which field drives bubbles + legend
    const groupField =
      regions.length ? 'region' :
        engine_model.length ? 'engine_model' :
          segment.length ? 'segment' :
            'region';

    const groupValues =
      regions.length ? regions :
        engine_model.length ? engine_model :
          segment.length ? segment :
            [];

    // --- REDUCED BUBBLE SIZE LOGIC ---
    const currentHeight = chartDom.clientHeight || 700;
    const sizeMultiplier = currentHeight / 300;

    // Build one series per group => legend items are dynamic
    const seriesData = groupValues.map((groupName: any, index: number) => {
      const groupItems = rawData.filter((d: any) => d[groupField] === groupName);

      const points = groupItems.map((item: any) => [
        Number(item.margin) / 1000000 <= 0 ? 0.1 : Number(item.margin) / 1000000,
        Number(item.percentage),
        Number(item.nrp_sales) / 1000000,
        item[groupField], // 3: Category Label
        item.margin || 0, // 4: Raw Margin
        item.percentage || 0, // 5: Raw Percentage
        item.nrp_sales || 0, // 6: Raw NRP Sales
        item.total || item.gross_sales || item.grossSales || item.nrp_sales || 0, // 7: Total/Gross
        item.year || 2025 // 8: Year
      ]);

      const baseColor =
        fallbackPalette[index % fallbackPalette.length];

      return {
        name: groupName, // legend name + series name (dynamic)
        type: 'scatter',
        data: points,

        symbolSize: (data: any) => {
          const val = Math.sqrt(data[2]) * sizeMultiplier;
          return Math.max(8, Math.min(val, currentHeight / 10));
        },

        itemStyle: {
          color: {
            type: 'radial',
            x: 0.3,
            y: 0.3,
            r: 0.8,
            colorStops: [
              { offset: 0, color: '#ffffff' },
              { offset: 0.15, color: baseColor },
              { offset: 1, color: baseColor },
            ],
          },
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.2)',
          shadowOffsetY: 4,
        },

        label: {
          show: true,
          formatter: (p: any) => p.data[3], // bubble label dynamic (region/engine_model/segment)
          position: 'top',
          fontSize: sc(9),
          color: '#333',
          fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        },

        emphasis: { focus: 'none' },

        ...(index === 0
          ? {
            markLine: {
              silent: true,
              symbol: 'none',
              label: {
                show: true,
                formatter: '65%',
                color: '#6b7280',
                fontSize: sc(11),
                fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
              },
              lineStyle: { type: 'dashed', color: '#6b7280', width: 1 },
              data: [{ yAxis: 65 }],
            },
          }
          : {}),
      };
    });

    const groupLabelMap: any = {
      'region': 'Region:',
      'segment': 'Segment:',
      'engine_model': 'Engine Model:'
    };
    const activeLabel = groupLabelMap[groupField] || 'Region:';

    const formatValue = (val: any) => {
      const num = Number(val);
      if (isNaN(num)) return '0';
      return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    };



    const option = {
      legend: { type: 'scroll', top: this.isFullScreen ? 80 : 0, icon: 'circle', textStyle: { fontSize: sc(11) } },
      grid: {
        top: this.isFullScreen ? 200 : '20%',
        left: this.isFullScreen ? 120 : 80,
        right: this.isFullScreen ? 120 : '8%',
        bottom: this.isFullScreen ? 160 : '15%',
        containLabel: true
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const d = params.data;
          return `<b>${activeLabel}</b> <small>${d[3]}</small><br/>
                  <b>Margin$:</b> ${formatValue(d[4])}<br/>
                  <b>Margin%:</b> ${Number(d[5]).toFixed(1)}%<br/>
                  <b>Total:</b> ${formatValue(d[7])}`;
        }
      },
      xAxis: {
        type: 'log',
        logBase: 10,
        name: this.data?.chartConfig?.xAxisLabel || 'Margin $',
        nameLocation: 'middle',
        nameGap: 40,
        min: 0.1,
        nameTextStyle: {
          color: '#666',
          fontSize: sc(12),
          fontWeight: 600,
          fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        },
        axisLabel: {
          formatter: (value: number) => (value === 0.1 ? '0M' : value + 'M'),
          color: '#888',
          fontSize: sc(11),
          fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        name: this.data?.chartConfig?.yAxisLabel || 'Margin %',
        nameLocation: 'middle',
        nameRotate: 90,
        nameGap: 40,
        min: 47,
        max: 80,
        nameTextStyle: {
          color: '#666',
          fontSize: sc(12),
          fontWeight: 600,
          fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        },
        // --- ADDED % SYMBOL TO Y-AXIS ---
        axisLabel: {
          formatter: '{value}%',
          color: '#888',
          fontSize: sc(11),
          fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
        },
        splitLine: { show: false }
      },
      series: seriesData
    };

    // Store sizing params at class level so legend handler can rebuild symbolSize
    this._sizeMultiplier = sizeMultiplier;
    this._maxBubbleSize = currentHeight / 10;
    this._seriesSnapshot = seriesData;

    this.myChart.setOption(option, true);
    this.bindChartFilterEvents();
  }

  private bindChartFilterEvents(): void {
    if (!this.myChart || !this.data?.filterKey) return;

    this.myChart.off('legendselectchanged');
    this.myChart.off('click');

    this.myChart.on('legendselectchanged', (params: any) => {
      if (this.isInternalLegendChange) return;

      const config = this.getLegendClonedConfig();
      const filterKey = this.getPrimaryFilterKey(config?.filterKey);
      if (!filterKey) {
        return;
      }

      const clickedLegend = params?.name;
      if (!clickedLegend || clickedLegend === 'Total') return;

      if (this.isCtrlPressed) {
        const idx = this.selectedLegends.indexOf(clickedLegend);
        if (idx >= 0) {
          this.selectedLegends = this.selectedLegends.filter((name) => name !== clickedLegend);
        } else {
          this.selectedLegends = [...this.selectedLegends, clickedLegend];
        }
      } else {
        this.selectedLegends =
          this.selectedLegends.length === 1 && this.selectedLegends[0] === clickedLegend
            ? []
            : [clickedLegend];
      }

      this.applyLegendSelectionState();

      if (this.selectedLegends.length === 0) {
        this.chartFilterHandler.updateFilterSelection(config, []);
        return;
      }

      const value =
        this.selectedLegends.length === 1 ? this.selectedLegends[0] : this.selectedLegends;

      const filterKeys: string[] = Array.isArray(config?.filterKey)
        ? config.filterKey
        : String(config?.filterKey || '').split(',').map((k: string) => k.trim()).filter(Boolean);

      const payload: any = {};
      filterKeys.forEach((key) => {
        if (key === 'year') {
          payload[key] = 2025; // Default for these overview screens
        } else {
          payload[key] = value;
        }
      });

      this.chartFilterHandler.updateFilterSelection(config, payload);
    });

    this.myChart.on('click', (params: any) => {
      const region = params?.data?.[3];
      const year = params?.data?.[8];

      if (!region) return;

      const filterKeys: string[] = Array.isArray(this.data?.filterKey)
        ? this.data.filterKey
        : String(this.data?.filterKey || '').split(',').map((k: string) => k.trim()).filter(Boolean);

      const payload: any = {};
      filterKeys.forEach((key) => {
        if (key === 'year') {
          payload[key] = year || 2025;
        } else {
          payload[key] = region;
        }
      });

      this.chartFilterHandler.updateFilterSelection(this.data, payload);
    });
  }

  private applyLegendSelectionState(): void {
    if (!this.myChart || this._seriesSnapshot.length === 0) return;

    const baseMult = this._sizeMultiplier;
    const maxSize = this._maxBubbleSize;
    const hasSelection = this.selectedLegends.length > 0;

    // Keep all legends visible
    const selectedMap: Record<string, boolean> = {};
    this._seriesSnapshot.forEach((s: any) => {
      selectedMap[s.name] = true;
    });

    // Build updated series with size/opacity changes
    const updatedSeries = this._seriesSnapshot.map((s: any) => {
      const isSelected = this.selectedLegends.includes(s.name);

      if (!hasSelection) {
        // No selection — restore normal sizes and full opacity
        return {
          name: s.name,
          symbolSize: (data: any) => {
            const val = Math.sqrt(data[2]) * baseMult;
            return Math.max(8, Math.min(val, maxSize));
          },
          itemStyle: {
            ...s.itemStyle,
            opacity: 1,
          },
          label: {
            ...s.label,
            color: '#333',
          },
        };
      }

      if (isSelected) {
        // Selected legend — enlarge bubbles by 1.8x
        return {
          name: s.name,
          symbolSize: (data: any) => {
            const val = Math.sqrt(data[2]) * baseMult * 3;
            return Math.max(12, Math.min(val, maxSize * 3));
          },
          itemStyle: {
            ...s.itemStyle,
            opacity: 1,
          },
          label: {
            ...s.label,
            color: '#333',
            fontWeight: 'bold' as const,
          },
        };
      } else {
        // Non-selected legend — shrink bubbles and fade
        return {
          name: s.name,
          symbolSize: (data: any) => {
            const val = Math.sqrt(data[2]) * baseMult * 0.7;
            return Math.max(6, Math.min(val, maxSize * 0.7));
          },
          itemStyle: {
            ...s.itemStyle,
            opacity: 0.25,
          },
          label: {
            ...s.label,
            color: '#bbb',
          },
        };
      }
    });

    this.isInternalLegendChange = true;
    this.myChart.setOption(
      {
        legend: { selected: selectedMap },
        series: updatedSeries,
      },
      { lazyUpdate: false },
    );
    this.isInternalLegendChange = false;
  }

  private getLegendClonedConfig(): any {
    const listener = this.data?.legendListener || this.data?.legendListner || this.data?.filterListener || this.data?.filterListner;
    const key = this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;
    return {
      ...this.data,
      ...(listener ? { filterListener: listener } : {}),
      ...(key ? { filterKey: key } : {}),
    };
  }

  private getPrimaryFilterKey(filterKeyConfig: any): string {
    const keys: string[] = Array.isArray(filterKeyConfig)
      ? filterKeyConfig
      : String(filterKeyConfig || '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);
    return keys[0] || '';
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = true;
    }
  }

  @HostListener('window:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = false;
    }
    if (event.key === 'Escape' && this.isFullScreen) {
      this.toggleFullScreen();
    }
  }

  protected override addResizeListener(): void {
    const element =
      this.chartContainer?.nativeElement || document.getElementById(this.data?.widgetId);
    if (!element) return;

    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.resizeObserver = new ResizeObserver(() => {
      if (this.myChart) {
        this.myChart.resize();
        const currentOption = this.myChart.getOption();
        if (currentOption) {
          this.myChart.setOption(currentOption);
        }
      }
    });
    this.resizeObserver.observe(element);
  }

  override ngOnDestroy(): void {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.myChart) this.myChart.dispose();
    document.documentElement.classList.remove('dvm-no-scroll');
    document.body.classList.remove('dvm-no-scroll');
    super.ngOnDestroy();
  }

  public downloadCsv(): void {
    const rows = this.rawBubbleData;
    if (!rows || !rows.length) return;
    const groupField = rows.some((d: any) => d.region) ? 'region' : rows.some((d: any) => d.engine_model) ? 'engine_model' : rows.some((d: any) => d.segment) ? 'segment' : 'region';
    const groupLabel = ({ region: 'Region', engine_model: 'Engine Model', segment: 'Segment' } as any)[groupField] || 'Group';
    const headers = [groupLabel, 'Year', 'Margin ($)', 'Margin (%)', 'NRP Sales'];
    const dataRows = rows.map((r: any) => [r[groupField] ?? '', r['year'] ?? '', Number(r['margin'] || 0), Number(r['percentage'] || 0), Number(r['nrp_sales'] || 0)]);
    
    downloadAsExcel(String(this.data?.widgetId || 'bubble'), headers, dataRows);
  }
}


